import _ from "lodash";
/* eslint-disable react/display-name */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  Suspense,
} from "react";
import { ReactMic } from "@toddle-edu/react-mic";

import classes from "./WithRecorder.scss";

import Loading from "UIComponents/Loading";

import { formatTimer, prepareAttachmentFromBlob } from "Utils";
import { Button, Modal } from "@toddle-edu/ds-web";
import { isExtendedAudioRecordingEnabled } from "Post/utils";
import AudioPlayer from "UIComponents/AudioPlayer";
import { DownloadOutlined } from "@toddle-edu/ds-icons";

const INTERVAL = 1;

const withRecorder = Component => ({
  autoStartRecording,
  draftRecording,
  limit = isExtendedAudioRecordingEnabled() ? 600000 : 300000,
  handleAudioStripVisibility,
  handleAudioFile,
  onCancel,
  onDraftModalOutsideClick,
  saveDraftRecording,
  updateAudioRecorderBlob,
  setToastMsg,
  showVisualiser,
  visualiserHeight,
  visualiserWidth,
  visualiserColor,
  frameRate,
  forPlayerVersion5 = false,
  playBeepOnStart = false,
  setIsInitializing,
  isInitializing,
  setChatbarVisible,

  ...props
}) => {
  // =====
  const { t, testId } = props;
  const intervalRef = useRef(null);
  const offsetRef = useRef(0);
  const clockRef = useRef(0);
  const [isPermissionGranted, setIsPermissionGranted] = useState(true);
  const [playerState, setPlayerState] = useState("record");
  const [currentAction, setCurrentDialogueAction] = useState("");
  const [timer, setTimer] = useState(0);
  const [isPaused, setPaused] = useState(false);
  const draftRecordingBlobRef = useRef(null);
  const draftblob = _.get(draftRecordingBlobRef.current, "blob", null);
  const calculatedPercent = Math.abs((timer * 100) / limit);
  const [pausedBlob, setPausedBlob] = useState(null);
  const [visualiserContainerRef, setVisualiserContainerRef] = useState(null);
  /**
   * When the recording is stopped and being uploaded the setPlayerState
   * function inside handleRecordedData is called once but, the callback
   * inside is called twice thus uploading the recording twice. To prevent
   * this we use a ref to check if the recording is already uploaded.
   */
  const isRecordingUploaded = useRef(false);

  // this state is used to show that we had a draft recording
  const [hadDraftRecording, setHadDraftRecording] = useState(false);
  // =====
  // =====
  /**
   * If auto start is true and there is no draft
   * recording then start the recording
   */

  const checkPermissions = () => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(stream => {
          setIsPermissionGranted(true);
          stream.stop();
        })
        .catch(() => {
          setIsPermissionGranted(false);
        });
    }
  };

  useEffect(() => {
    checkPermissions();
    if (autoStartRecording && !draftRecording) {
      startRecording();
    }
    // clear interval on unmount
    return () => {
      if (intervalRef && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPermissionGranted]);

  /**
   * Check if isPaused is changed
   * and clear the interval or start
   * the time interval again
   */
  useEffect(() => {
    if (
      isPaused &&
      playerState === "recording" &&
      intervalRef &&
      intervalRef.current
    ) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else if (
      !isPaused &&
      playerState === "recording" &&
      intervalRef &&
      !intervalRef.current
    ) {
      offsetRef.current = Date.now();
      intervalRef.current = setInterval(updateTime, INTERVAL);
    }
  }, [isPaused]);

  useEffect(() => {
    if (draftRecording) {
      setCurrentDialogueAction("DRAFT_RECORDING");
    }
  }, [draftRecording]);

  useEffect(() => {
    if (timer >= limit) stopRecording();
  }, [timer]);

  useEffect(() => {
    if (playerState === "cancelled" && handleAudioStripVisibility) {
      handleAudioStripVisibility();
    }
  }, [playerState]);

  const startRecording = useCallback(async () => {
    // add method to start/stope animation here
    checkPermissions();
    setPlayerState("recording");
  }, []);

  const stopRecording = useCallback(() => {
    if (setChatbarVisible) setChatbarVisible(true);
    if (playerState === "recording") {
      setPlayerState("completed");
    }
    stopTimer();
  }, [playerState]);

  const cancelRecording = useCallback(async () => {
    await stopTimer();
    await setPlayerState("cancelled");
    onCancel();
  }, []);

  const startTimer = () => {
    if (setIsInitializing) {
      setIsInitializing(false);
    }
    if (!isPaused) {
      offsetRef.current = Date.now();
      intervalRef.current = setInterval(updateTime, INTERVAL);
    }
  };

  const stopTimer = () => {
    clearInterval(intervalRef.current);
    setTimer(0);
  };

  /**
   * Calculate the elapsed time
   */
  const delta = () => {
    const now = Date.now();
    const d = now - offsetRef.current;
    offsetRef.current = now;
    return d;
  };

  const updateTime = () => {
    clockRef.current += delta();
    setTimer(clockRef.current);
  };

  // update audio blob on pause
  const handleRecordedBlob = React.useCallback(async blob => {
    blob.then(data => {
      setPausedBlob(data);
    });
  }, []);

  // toggle pause and update the blob when component toggles pause state
  const togglePauseAndUpdateBlob = pausedState => {
    if (!pausedState) {
      setPausedBlob(null);
    }
    setPaused(pausedState);
  };

  // save audio blob in redux when react-mic unmounts
  const onMicUnmount = blob => {
    if (!saveDraftRecording) {
      return;
    }
    blob["duration"] = clockRef.current;
    updateAudioRecorderBlob(blob);

    setToastMsg("common:save_audio_as_draft");
  };

  const handleRecordedData = useCallback(recordedBlob => {
    // handle the stale playerState due to async onStop and closure
    setPlayerState(state => {
      if (state === "completed" && !isRecordingUploaded.current) {
        isRecordingUploaded.current = true;
        try {
          // clockRef holds the elapsed ms; timer state is reset to 0 by
          // stopTimer() before onStop fires, so it can't be used here.
          const attachment = prepareAttachmentFromBlob(
            recordedBlob,
            clockRef.current
          );
          handleAudioFile(attachment);
        } catch {}
      }
      // No state change
      updateAudioRecorderBlob(null);
      return state;
    });
  }, []);
  // =====

  const onClickContinueButton = () => {
    const { duration } = draftRecording;
    switch (currentAction) {
      case "DRAFT_RECORDING":
        // pause the recording and let user continue from previous draft recording
        setPaused(true);
        clockRef.current = duration;
        setTimer(clockRef.current);
        draftRecordingBlobRef.current = draftRecording;
        updateAudioRecorderBlob(null);
        setPlayerState("recording");
        setHadDraftRecording(true);
        break;
    }
  };
  const onClickStartNewButton = async () => {
    switch (currentAction) {
      case "DRAFT_RECORDING":
        draftRecordingBlobRef.current = null;
        updateAudioRecorderBlob(null);
        clockRef.current = 0;
        setPlayerState("recording");
        setHadDraftRecording(true);
        break;
    }
  };

  const audioPlayerRef = useRef(null);

  const draftModal = () => {
    const attachment = prepareAttachmentFromBlob(draftRecording);

    const handleDownload = e => {
      // Create a blob URL from the draft recording
      e.stopPropagation();
      const url = draftRecording.blobURL;

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
      const fileName = `toddle_audio_${currentDate}.mp3`;

      // Create a temporary download link
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = fileName;

      // Trigger the download
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Clean up
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    };

    const handleAudioPlayPause = () => {
      if (!audioPlayerRef.current) return;

      audioPlayerRef.current.toggleAudio();
    };

    return (
      <Modal height="fit-content" width="fit-content" dsVersion="2.0">
        <Modal.Header
          isClosable={false}
          title={t("common:audio_recorder")}
          subtitle={t("common:unused_recording")}
        ></Modal.Header>
        <Modal.Body>
          <div
            className={classes.mediaPlaceholderContainer}
            onClick={handleAudioPlayPause}
          >
            <AudioPlayer attachment={attachment} ref={audioPlayerRef} />
            <DownloadOutlined
              size="x-small"
              className={classes.downloadIcon}
              onClick={handleDownload}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.modalFooterButton}>
            <Button
              variant="progressive"
              _legacy_size="large"
              size="medium"
              onClick={onClickContinueButton}
              testId={`${testId}-withRecorder-continueRecordingModal`}
            >
              {t("common:continue_with_this_recording")}
            </Button>
            <Button
              _legacy_size="large"
              size="medium"
              onClick={onClickStartNewButton}
              testId={`${testId}-withRecorder-startNewRecording`}
            >
              {t("common:discard_and_record_new")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  useEffect(() => {
    if (draftRecording) {
      setCurrentDialogueAction("DRAFT_RECORDING");
    }
    if (!!draftRecording && forPlayerVersion5) {
      const { duration } = draftRecording;
      setPaused(true);
      clockRef.current = duration;
      setTimer(clockRef.current);
      draftRecordingBlobRef.current = draftRecording;
      updateAudioRecorderBlob(null);
      setPlayerState("recording");
      setHadDraftRecording(true);
    }
  }, [draftRecording]);

  const reactMicClasses =
    isInitializing || !showVisualiser ? classes.hideReactMicUI : "";

  return (
    <Suspense fallback={<Loading />}>
      {currentAction &&
        !!draftRecording &&
        saveDraftRecording &&
        !forPlayerVersion5 &&
        draftModal()}
      {(!draftRecording || (hadDraftRecording && forPlayerVersion5)) && (
        <>
          <ReactMic
            record={isPermissionGranted && playerState === "recording"}
            className={reactMicClasses}
            onStop={handleRecordedData}
            onPause={handleRecordedBlob}
            mimeType="audio/mp3"
            onStart={startTimer}
            onUnmount={onMicUnmount}
            isPaused={isPaused}
            draftRecordingBlob={draftblob}
            visualSetting="amplitudeBars"
            height={visualiserHeight}
            width={visualiserWidth}
            strokeColor={visualiserColor}
            visualizerContainerRef={visualiserContainerRef}
            frameRate={frameRate}
            playBeepOnStart={playBeepOnStart}
          />
          <Component
            isPaused={isPaused}
            togglePause={togglePauseAndUpdateBlob}
            isInitializing={isInitializing}
            pauseStateBlob={pausedBlob}
            draftRecordingBlob={draftRecordingBlobRef.current}
            hadDraftRecording={hadDraftRecording}
            timer={formatTimer(timer)}
            formattedLimit={formatTimer(limit)}
            isPermissionGranted={isPermissionGranted}
            playerState={playerState}
            startRecording={startRecording}
            stopRecording={stopRecording}
            cancelRecording={cancelRecording}
            calculatedPercent={calculatedPercent}
            timeStamp={timer}
            onDraftModalOutsideClick={onDraftModalOutsideClick}
            saveDraftRecording={saveDraftRecording}
            handleVisualiserRefUpdate={setVisualiserContainerRef}
            onClickContinueButton={onClickContinueButton}
            onClickStartNewButton={onClickStartNewButton}
            handleAudioFile={handleAudioFile}
            {...props}
          />
        </>
      )}
    </Suspense>
  );
};

export default withRecorder;
